/**
 * Permission Resolver Service
 * 
 * Resolves permissions for users at specific scopes by:
 * 1. Fetching the user's direct role assignments
 * 2. Fetching the user's group memberships and their role assignments
 * 3. Walking up the scope hierarchy (most specific to least specific)
 * 4. Applying "most specific wins" inheritance rule
 * 
 * This is the client-side permission resolver. For server-side (Supabase RLS),
 * see the user_has_permission() function in the migrations.
 */

import { getSupabaseClient } from './supabaseClient';
import type {
  PermissionAction,
  PermissionScope,
  ScopedRoleAssignment,
  EffectivePermission,
  GroupMembership,
  GroupType,
  PermissionCheckResult,
  Policy,
  PolicyEvaluationResult,
  ScopeKind,
} from '../types/prioritization/permissions';
import { flattenScopeChain } from '../types/prioritization/permissions';
import type { CapabilityId } from '../types/prioritization/capabilityIds';
import {
  isCapabilityId,
  getCapabilitiesForAction,
  getActionsGrantedByCapability,
  PERMISSION_ACTION_TO_CAPABILITIES,
} from '../types/prioritization/capabilityIds';
import type { ScopeReach } from '../types/prioritization/roles';
import type { SpaceMembershipRole } from '../types/prioritization/collaborationSpaces';
import { SPACE_ROLE_CAPABILITIES, spaceRoleGrantsCapability } from '../types/prioritization/collaborationSpaces';
import { evaluateSpacePolicies, type SpacePolicyContext } from './spacePolicies';

// ============================================================================
// Types for internal use
// ============================================================================

/** Minimal row type for raw Supabase select results where generated types are unavailable. */
type RawDbRow = Record<string, unknown>;

/** Runtime store: role_permissions.permission holds capability IDs. Legacy permission strings are normalized to capabilities at load time. */
interface CachedPermissions {
  userId: string;
  roleAssignments: ScopedRoleAssignment[];
  groupMemberships: GroupMembership[];
  /** role_id -> set of capability IDs (and legacy permissions normalized to capabilities). */
  rolePermissions: Map<string, Set<CapabilityId>>;
  /**
   * role_id -> cascade_mode from the roles table.
   * 'none'     → grant only at exact scope
   * 'children' → grant at assigned scope and all descendants
   * 'all'      → grant everywhere (admin roles)
   * Missing entry defaults to 'none'.
   */
  roleScopeReach: Map<string, ScopeReach>;
  loadedAt: Date;
  /** The rights could not be read, so this says nothing about what the person may do. */
  loadFailed?: boolean;
}

// ============================================================================
// Permission Cache
// ============================================================================

let permissionCache: CachedPermissions | null = null;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

function isCacheValid(userId: string): boolean {
  if (!permissionCache) return false;
  if (permissionCache.userId !== userId) return false;
  const age = Date.now() - permissionCache.loadedAt.getTime();
  return age < CACHE_TTL_MS;
}

export function clearPermissionCache(): void {
  permissionCache = null;
}

// ============================================================================
// Data Loading
// ============================================================================

/**
 * Load all permission-related data for a user from Supabase
 */
export async function loadUserPermissionData(userId: string): Promise<CachedPermissions> {
  if (!userId) {
    return {
      userId: '',
      roleAssignments: [],
      groupMemberships: [],
      rolePermissions: new Map(),
      roleScopeReach: new Map(),
      loadedAt: new Date(),
    };
  }
  if (isCacheValid(userId) && permissionCache) {
    return permissionCache;
  }

  const supabase = getSupabaseClient();

  // Load in parallel for performance
  const [
    roleAssignmentsResult,
    groupMembershipsResult,
    rolePermissionsResult,
    rolesResult,
    adminMembershipsResult,
  ] = await Promise.all([
    // User's direct role assignments
    supabase
      .from('role_assignments')
      .select('*')
      .eq('subject_type', 'user')
      .eq('subject_id', userId),
    
    // User's group memberships — groups table has no "type" column; select only existing columns
    supabase
      .from('memberships')
      .select('id,user_id,group_id,created_at,groups:group_id(id,name)')
      .eq('user_id', userId),
    
    // All role-permission mappings (column is "permission" in DB)
    supabase
      .from('role_permissions')
      .select('role_id, permission'),

    // Role cascade modes — drives whether permissions propagate through the org hierarchy
    supabase
      .from('roles')
      .select('id, name, cascade_mode'),

    // Workspaces this person owns or administers by membership
    supabase
      .from('workspace_memberships')
      .select('id, workspace_id, created_at')
      .eq('user_id', userId)
      .eq('status', 'active')
      .in('role', ['owner', 'admin']),
  ]);

  if (groupMembershipsResult.error) {
    console.error('[PermissionResolver] memberships load failed', {
      message: groupMembershipsResult.error.message,
      details: groupMembershipsResult.error.details ?? null,
      hint: groupMembershipsResult.error.hint ?? null,
      code: groupMembershipsResult.error.code ?? null,
    });
  }

  // Also load group-based role assignments
  const groupIds = (groupMembershipsResult.data || []).map((m: { group_id: string }) => m.group_id);
  let groupRoleAssignmentsResult: { data: RawDbRow[] | null; error: unknown } = { data: [] as RawDbRow[], error: null };
  
  if (groupIds.length > 0) {
    groupRoleAssignmentsResult = await supabase
      .from('role_assignments')
      .select('*')
      .eq('subject_type', 'group')
      .in('subject_id', groupIds);
  }

  // Transform role assignments
  const directAssignments: ScopedRoleAssignment[] = (roleAssignmentsResult.data || []).map((ra: RawDbRow) => ({
    id: ra.id as string,
    subjectType: 'user' as const,
    subjectId: ra.subject_id as string,
    roleId: ra.role_id as string,
    roleLabel: ra.role_id as string,
    scopeKind: ra.scope_kind as ScopeKind,
    scopeId: ra.scope_id as string,
    scopePath: (ra.scope_path as string[] | null) ?? undefined, // DB column is a text array (SUPABASE_SCHEMA_LIVE.md), not a scalar string
    inherited: (ra.inherited as boolean) || false,
    grantedBy: ra.granted_by as string, // NOT NULL in DB (SUPABASE_SCHEMA_LIVE.md)
    grantedAt: new Date(ra.created_at as string),
    expiresAt: ra.expires_at ? new Date(ra.expires_at as string) : undefined,
  }));

  const groupAssignments: ScopedRoleAssignment[] = (groupRoleAssignmentsResult.data || []).map((ra: RawDbRow) => ({
    id: ra.id as string,
    subjectType: 'group' as const,
    subjectId: ra.subject_id as string,
    roleId: ra.role_id as string,
    roleLabel: ra.role_id as string,
    scopeKind: ra.scope_kind as ScopeKind,
    scopeId: ra.scope_id as string,
    scopePath: (ra.scope_path as string[] | null) ?? undefined, // DB column is a text array (SUPABASE_SCHEMA_LIVE.md), not a scalar string
    inherited: true,
    grantedBy: ra.granted_by as string, // NOT NULL in DB (SUPABASE_SCHEMA_LIVE.md)
    grantedAt: new Date(ra.created_at as string),
    expiresAt: ra.expires_at ? new Date(ra.expires_at as string) : undefined,
  }));

  // Transform group memberships (is_primary not in DB; default false)
  const memberships: GroupMembership[] = (groupMembershipsResult.data || []).map((m: RawDbRow) => ({
    id: m.id as string,
    userId: m.user_id as string,
    groupId: m.group_id as string,
    groupName: (m.groups as { name?: string } | null)?.name || 'Unknown',
    // groups table has no "type" column (see query above — not selected), so
    // this always falls back to 'team' today. Cast documents the intended
    // GroupType, not a behavior change.
    groupType: ((m.groups as { type?: string } | null)?.type || 'team') as GroupType,
    isPrimary: (m.is_primary as boolean | null) ?? false,
    joinedAt: new Date(m.created_at as string),
  }));

  // Build role -> capability set (permission column stores capability IDs; legacy strings normalized to capabilities)
  const rolePermissions = new Map<string, Set<CapabilityId>>();
  for (const rp of (rolePermissionsResult.data || [])) {
    // Query selects 'role_id, permission' only (see query above) — permission_id
    // was never a real column here, just a dead defensive fallback.
    const perm = rp.permission as string;
    if (!rolePermissions.has(rp.role_id)) {
      rolePermissions.set(rp.role_id, new Set());
    }
    const set = rolePermissions.get(rp.role_id)!;
    if (isCapabilityId(perm)) {
      set.add(perm);
    } else if (perm in PERMISSION_ACTION_TO_CAPABILITIES) {
      for (const cap of getCapabilitiesForAction(perm as PermissionAction)) {
        set.add(cap);
      }
    }
  }

  // Build role -> cascade_mode map
  const roleScopeReach = new Map<string, ScopeReach>();
  for (const r of (rolesResult.data || [])) {
    const mode = (r.cascade_mode as ScopeReach) || 'none';
    roleScopeReach.set(r.id, mode);
  }

  // An owner or admin membership carries the workspace-admin role, as it does on
  // the server (internal_rpc_actor_is_admin) and in userStore.can(). The only
  // assignment such a person holds may be the workspace-member one the membership
  // trigger writes, and without this the "New Cycle" button was drawn for an owner
  // and the same action refused when they submitted the form.
  const workspaceAdminRoleId = (rolesResult.data || []).find((r) => r.name === 'workspace-admin')?.id;
  const membershipAssignments: ScopedRoleAssignment[] = workspaceAdminRoleId
    ? (adminMembershipsResult.data || []).map((m) => ({
        id: m.id,
        subjectType: 'user' as const,
        subjectId: userId,
        roleId: workspaceAdminRoleId,
        roleLabel: workspaceAdminRoleId,
        scopeKind: 'resource' as ScopeKind,
        scopeId: m.workspace_id,
        inherited: false,
        grantedBy: 'system:workspace-membership',
        grantedAt: new Date(m.created_at ?? Date.now()),
      }))
    : [];

  const loaded: CachedPermissions = {
    userId,
    roleAssignments: [...directAssignments, ...groupAssignments, ...membershipAssignments],
    groupMemberships: memberships,
    rolePermissions,
    roleScopeReach,
    loadedAt: new Date(),
  };

  // On a page load the first read can go out before the sign-in is attached and
  // come back 401. Keeping that empty answer left a person with no rights for the
  // cache's five minutes, refused acts they hold, with the button still drawn.
  if (roleAssignmentsResult.error || rolePermissionsResult.error || rolesResult.error) {
    return { ...loaded, loadFailed: true };
  }

  permissionCache = loaded;
  return loaded;
}

// ============================================================================
// Permission Resolution
// ============================================================================

/**
 * Check if a user has a capability at a scope (capability-based permission check).
 *
 * Uses role cascade_mode to decide how assignments propagate:
 *   'all'      → assignment anywhere grants access everywhere
 *   'children' → assignment at an ancestor scope grants access at descendants
 *   'none'     → assignment must be at the exact scope requested
 */
export async function hasCapability(
  userId: string,
  capabilityId: CapabilityId,
  scope: PermissionScope
): Promise<boolean> {
  const data = await loadUserPermissionData(userId);
  const scopeChain = flattenScopeChain(scope); // most specific → least specific
  const now = new Date();

  for (const assignment of data.roleAssignments) {
    if (assignment.expiresAt && assignment.expiresAt <= now) continue;
    if (!data.rolePermissions.get(assignment.roleId)?.has(capabilityId)) continue;

    const scopeReach = data.roleScopeReach.get(assignment.roleId) ?? 'none';

    if (scopeReach === 'all') {
      return true;
    }

    // 'global' assignments are workspace-wide wildcards — apply everywhere.
    if (assignment.scopeKind === 'global') {
      return true;
    }

    if (scopeReach === 'children') {
      // Grant if the assignment is at the exact scope or at any ancestor scope in the chain
      const matchesExact = assignment.scopeKind === scope.kind && assignment.scopeId === scope.id;
      const matchesAncestor = scopeChain.some(
        (s) => assignment.scopeKind === s.kind && (assignment.scopeId === s.id || assignment.scopeId === '*')
      );
      if (matchesExact || matchesAncestor) return true;
    }

    if (scopeReach === 'none') {
      // Grant if the assignment matches the exact scope, or if scope_id='*' (wildcard for all
      // resources of this scope kind — e.g. "Workspace-wide" Session Facilitator assignments).
      if (assignment.scopeKind === scope.kind &&
          (assignment.scopeId === scope.id || assignment.scopeId === '*')) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Check if a user has a specific permission at a scope (resolved via capability IDs).
 *
 * Uses role cascade_mode to determine whether an assignment at a broader scope
 * grants permission at the requested scope:
 *   'all'      → any assignment anywhere grants access
 *   'children' → assignment at the requested scope or any ancestor grants access
 *   'none'     → only an exact scope match grants access (deny-by-default)
 */
export async function checkPermission(
  userId: string,
  action: PermissionAction,
  scope: PermissionScope
): Promise<PermissionCheckResult> {
  const caps = getCapabilitiesForAction(action);
  if (caps.length === 0) {
    return {
      allowed: false,
      action,
      scope,
      reason: 'No capability mapping for this action',
    };
  }

  const data = await loadUserPermissionData(userId);
  const scopeChain = flattenScopeChain(scope); // most specific → least specific
  const now = new Date();

  for (const assignment of data.roleAssignments) {
    if (assignment.expiresAt && assignment.expiresAt <= now) continue;

    const scopeReach = data.roleScopeReach.get(assignment.roleId) ?? 'none';
    const roleCaps = data.rolePermissions.get(assignment.roleId);
    if (!roleCaps) continue;

    const hasMatchingCap = caps.some((cap) => roleCaps.has(cap));
    if (!hasMatchingCap) continue;

    // Determine whether this assignment applies at the requested scope
    let appliesHere = false;
    let sourceScope = scope;

    if (scopeReach === 'all') {
      appliesHere = true;
      sourceScope = { kind: assignment.scopeKind, id: assignment.scopeId ?? '*' };
    } else if (assignment.scopeKind === 'global') {
      // 'global' assignments are workspace-wide wildcards — apply everywhere.
      appliesHere = true;
      sourceScope = { kind: assignment.scopeKind, id: assignment.scopeId ?? '*' };
    } else if (scopeReach === 'children') {
      // Exact match or ancestor in scope chain
      const exactMatch = assignment.scopeKind === scope.kind && assignment.scopeId === scope.id;
      const ancestorMatch = scopeChain.find(
        (s) => assignment.scopeKind === s.kind && (assignment.scopeId === s.id || assignment.scopeId === '*')
      );
      if (exactMatch || ancestorMatch) {
        appliesHere = true;
        sourceScope = exactMatch ? scope : (ancestorMatch ?? scope);
      }
    } else {
      // cascade_mode = 'none': exact scope only
      if (assignment.scopeKind === scope.kind && assignment.scopeId === scope.id) {
        appliesHere = true;
      }
    }

    if (appliesHere) {
      const matchedCap = caps.find((cap) => roleCaps.has(cap))!;
      return {
        allowed: true,
        action,
        scope,
        effectivePermission: {
          action,
          granted: true,
          sourceScope,
          sourceRole: assignment.roleId,
          inherited: sourceScope.kind !== scope.kind,
        },
        reason: `Granted by capability ${matchedCap} via role (cascade_mode=${scopeReach}) at ${sourceScope.kind} scope`,
      };
    }
  }

  return {
    allowed: false,
    action,
    scope,
    reason: 'No role assignment grants the required capability at the requested scope (deny-by-default)',
  };
}

/**
 * Get the set of capability IDs the user has at the given scope (from DB role_permissions only).
 * Source of truth: UI-configured roles + role_permissions. No YAML.
 *
 * Respects cascade_mode: only includes capabilities from assignments that apply at this scope.
 */
export async function getResolvedCapabilitiesForUser(
  userId: string,
  scope: PermissionScope
): Promise<CapabilityId[]> {
  if (!userId) return [];
  const data = await loadUserPermissionData(userId);
  const scopeChain = flattenScopeChain(scope);
  const capSet = new Set<CapabilityId>();
  const now = new Date();

  for (const assignment of data.roleAssignments) {
    if (assignment.expiresAt && assignment.expiresAt <= now) continue;

    const scopeReach = data.roleScopeReach.get(assignment.roleId) ?? 'none';
    let appliesHere = false;

    if (scopeReach === 'all') {
      appliesHere = true;
    } else if (assignment.scopeKind === 'global') {
      // 'global' assignments are workspace-wide wildcards — they apply to any scope being checked,
      // including 'resource' (workspace), 'organization', or 'children' scopes.
      appliesHere = true;
    } else if (scopeReach === 'children') {
      appliesHere =
        (assignment.scopeKind === scope.kind && assignment.scopeId === scope.id) ||
        scopeChain.some(
          (s) => assignment.scopeKind === s.kind && (assignment.scopeId === s.id || assignment.scopeId === '*')
        );
    } else {
      // scope_id='*' is a wildcard meaning "any resource of this scope kind" (e.g. Workspace-wide).
      appliesHere = assignment.scopeKind === scope.kind &&
        (assignment.scopeId === scope.id || assignment.scopeId === '*');
    }

    if (appliesHere) {
      const caps = data.rolePermissions.get(assignment.roleId);
      if (caps) caps.forEach((c) => capSet.add(c));
    }
  }

  return Array.from(capSet);
}

/**
 * Get all effective permissions for a user at a scope (derived from capabilities).
 *
 * Processes assignments from least specific to most specific so that more specific
 * grants overwrite broader ones ("most specific wins"). Respects cascade_mode.
 */
export async function getEffectivePermissions(
  userId: string,
  scope: PermissionScope
): Promise<Map<PermissionAction, EffectivePermission>> {
  const data = await loadUserPermissionData(userId);
  const scopeChain = flattenScopeChain(scope);
  const permissions = new Map<PermissionAction, EffectivePermission>();
  const now = new Date();

  // Sort assignments from least specific to most specific so later (more specific) entries win
  // We process 'all' first, then 'children', then 'none' (exact) — mirroring specificity order.
  const sortOrder: ScopeReach[] = ['all', 'children', 'none'];

  for (const modeFilter of sortOrder) {
    for (const assignment of data.roleAssignments) {
      if (assignment.expiresAt && assignment.expiresAt <= now) continue;

      const scopeReach = data.roleScopeReach.get(assignment.roleId) ?? 'none';
      if (scopeReach !== modeFilter) continue;

      let appliesHere = false;
      let sourceScope = scope;

      if (scopeReach === 'all') {
        appliesHere = true;
        sourceScope = { kind: assignment.scopeKind, id: assignment.scopeId ?? '*' };
      } else if (assignment.scopeKind === 'global') {
        // 'global' assignments are workspace-wide wildcards — they apply to any scope being checked.
        appliesHere = true;
        sourceScope = { kind: assignment.scopeKind, id: assignment.scopeId ?? '*' };
      } else if (scopeReach === 'children') {
        const exactMatch = assignment.scopeKind === scope.kind && assignment.scopeId === scope.id;
        const ancestorMatch = scopeChain.find(
          (s) => assignment.scopeKind === s.kind && (assignment.scopeId === s.id || assignment.scopeId === '*')
        );
        if (exactMatch || ancestorMatch) {
          appliesHere = true;
          sourceScope = exactMatch ? scope : (ancestorMatch ?? scope);
        }
      } else {
        // scope_id='*' is a wildcard meaning "any resource of this scope kind" (e.g. Workspace-wide).
        if (assignment.scopeKind === scope.kind &&
            (assignment.scopeId === scope.id || assignment.scopeId === '*')) {
          appliesHere = true;
        }
      }

      if (!appliesHere) continue;

      const caps = data.rolePermissions.get(assignment.roleId);
      if (!caps) continue;

      for (const cap of caps) {
        for (const action of getActionsGrantedByCapability(cap)) {
          permissions.set(action, {
            action,
            granted: true,
            sourceScope,
            sourceRole: assignment.roleId,
            inherited: sourceScope.kind !== scope.kind,
          });
        }
      }
    }
  }

  return permissions;
}

/**
 * Quick check for multiple permissions (returns true if ANY are granted)
 */
export async function hasAnyPermission(
  userId: string,
  actions: PermissionAction[],
  scope: PermissionScope
): Promise<boolean> {
  for (const action of actions) {
    const result = await checkPermission(userId, action, scope);
    if (result.allowed) return true;
  }
  return false;
}

/**
 * Quick check for multiple permissions (returns true if ALL are granted)
 */
export async function hasAllPermissions(
  userId: string,
  actions: PermissionAction[],
  scope: PermissionScope
): Promise<boolean> {
  for (const action of actions) {
    const result = await checkPermission(userId, action, scope);
    if (!result.allowed) return false;
  }
  return true;
}

// ============================================================================
// Role Assignment Management
// ============================================================================

/**
 * Get all role assignments for a user (direct and via groups)
 */
export async function getUserRoleAssignments(userId: string): Promise<ScopedRoleAssignment[]> {
  const data = await loadUserPermissionData(userId);
  return data.roleAssignments;
}

/**
 * Get all group memberships for a user
 */
export async function getUserGroupMemberships(userId: string): Promise<GroupMembership[]> {
  const data = await loadUserPermissionData(userId);
  return data.groupMemberships;
}

// ============================================================================
// Policy Evaluation
// ============================================================================

/**
 * Evaluate policies for an action at a scope
 */
export async function evaluatePolicies(
  userId: string,
  action: PermissionAction,
  scope: PermissionScope
): Promise<PolicyEvaluationResult> {
  const supabase = getSupabaseClient();
  
  // Get applicable policies for this scope
  const scopeChain = flattenScopeChain(scope);
  const scopeConditions = scopeChain.map(s => 
    `(scope_kind = '${s.kind}' AND (scope_id = '${s.id}' OR scope_id = '*'))`
  );
  
  const { data: policies, error } = await supabase
    .from('policies')
    .select('*')
    .eq('enabled', true)
    .or(scopeConditions.join(','))
    .order('priority', { ascending: false });
  
  if (error || !policies) {
    return {
      allowed: true,
      policies: [],
      violations: [],
      warnings: [],
    };
  }
  
  const violations: { policy: Policy; reason: string }[] = [];
  const warnings: { policy: Policy; message: string }[] = [];
  
  for (const policy of policies) {
    const p: Policy = {
      id: policy.id,
      name: policy.name,
      description: policy.description,
      scopeKind: policy.scope_kind,
      scopeId: policy.scope_id,
      ruleType: policy.rule_type,
      ruleConfig: policy.rule_config,
      enabled: policy.enabled,
      priority: policy.priority,
      createdBy: policy.created_by,
      createdAt: new Date(policy.created_at),
      updatedAt: new Date(policy.updated_at),
    };
    
    // Evaluate policy based on rule type
    switch (p.ruleType) {
      case 'require_approval':
        // Check if action is in the list requiring approval
        if ((p.ruleConfig.actions as string[] | undefined)?.includes(action)) {
          warnings.push({
            policy: p,
            message: `Action "${action}" requires approval`,
          });
        }
        break;
        
      case 'time_bound': {
        // Check if current time is within allowed window
        const now = new Date();
        const startTime = p.ruleConfig.startTime ? new Date(p.ruleConfig.startTime as string) : null;
        const endTime = p.ruleConfig.endTime ? new Date(p.ruleConfig.endTime as string) : null;
        
        if (startTime && now < startTime) {
          violations.push({
            policy: p,
            reason: `Action not allowed before ${startTime.toISOString()}`,
          });
        }
        if (endTime && now > endTime) {
          violations.push({
            policy: p,
            reason: `Action not allowed after ${endTime.toISOString()}`,
          });
        }
        break;
      }
        
      case 'require_evidence':
        // Note: actual evidence check would happen elsewhere
        if ((p.ruleConfig.actions as string[] | undefined)?.includes(action)) {
          warnings.push({
            policy: p,
            message: `Action "${action}" requires supporting evidence`,
          });
        }
        break;
        
      case 'audit_required':
        // Just flag for logging
        warnings.push({
          policy: p,
          message: `Action "${action}" will be logged for audit`,
        });
        break;
    }
  }
  
  return {
    allowed: violations.length === 0,
    policies: policies as Policy[],
    violations,
    warnings,
  };
}

// ============================================================================
// Synchronous Permission Check (for UI)
// ============================================================================

/**
 * Synchronous permission check using cached data.
 * Resolves action to capability IDs and checks if any assigned role has any of those capabilities.
 * Returns false if cache is not loaded.
 *
 * Respects cascade_mode: 'all' grants everywhere, 'children' grants at scope or ancestor,
 * 'none' grants only at the exact scope (deny-by-default).
 */
export function canSync(
  action: PermissionAction,
  scope: PermissionScope
): boolean {
  if (!permissionCache) return false;

  const requiredCaps = getCapabilitiesForAction(action);
  if (requiredCaps.length === 0) return false;

  const scopeChain = flattenScopeChain(scope);
  const now = new Date();

  for (const assignment of permissionCache.roleAssignments) {
    if (assignment.expiresAt && assignment.expiresAt <= now) continue;

    const roleCaps = permissionCache.rolePermissions.get(assignment.roleId);
    if (!roleCaps || !requiredCaps.some((cap) => roleCaps.has(cap))) continue;

    const scopeReach = permissionCache.roleScopeReach.get(assignment.roleId) ?? 'none';

    if (scopeReach === 'all') {
      return true;
    }

    // 'global' assignments are workspace-wide wildcards — apply everywhere.
    if (assignment.scopeKind === 'global') {
      return true;
    }

    if (scopeReach === 'children') {
      const exactMatch = assignment.scopeKind === scope.kind && assignment.scopeId === scope.id;
      const ancestorMatch = scopeChain.some(
        (s) => assignment.scopeKind === s.kind && (assignment.scopeId === s.id || assignment.scopeId === '*')
      );
      if (exactMatch || ancestorMatch) return true;
    }

    if (scopeReach === 'none') {
      // scope_id='*' is a wildcard meaning "any resource of this scope kind" (e.g. Workspace-wide).
      if (assignment.scopeKind === scope.kind &&
          (assignment.scopeId === scope.id || assignment.scopeId === '*')) {
        return true;
      }
    }
  }

  return false;
}

// ============================================================================
// Space-Aware Permission Helpers
// ============================================================================

/** Cache: spaceId → { userId → role } */
const spaceMembershipCache = new Map<string, Map<string, SpaceMembershipRole>>();

async function fetchSpaceMembershipRole(
  userId: string,
  spaceId: string
): Promise<SpaceMembershipRole | null> {
  const spaceCache = spaceMembershipCache.get(spaceId);
  if (spaceCache?.has(userId)) return spaceCache.get(userId)!;

  try {
    const supabase = getSupabaseClient();
    const { data } = await supabase
      .from('space_memberships')
      .select('role')
      .eq('space_id', spaceId)
      .eq('user_id', userId)
      .eq('status', 'active')
      .single();

    const role = (data?.role as SpaceMembershipRole) ?? null;
    if (role) {
      if (!spaceMembershipCache.has(spaceId)) {
        spaceMembershipCache.set(spaceId, new Map());
      }
      spaceMembershipCache.get(spaceId)!.set(userId, role);
    }
    return role;
  } catch {
    return null;
  }
}

/** Clear the space membership cache (call after membership changes). */
export function clearSpaceMembershipCache(spaceId?: string): void {
  if (spaceId) {
    spaceMembershipCache.delete(spaceId);
  } else {
    spaceMembershipCache.clear();
  }
}

/**
 * Optional caller-supplied signals used by policy evaluation
 * (e.g. approval token from an approval UI, or evidence the user provided).
 */
export interface SpacePermissionOptions {
  /** When true, signals that an approval has already been obtained (for require_approval policies). */
  approvalGranted?: boolean;
  /** Evidence the caller can provide for require_evidence policies. */
  evidence?: Record<string, unknown>;
  /** Skip space policy evaluation entirely (e.g. for read-only displays). */
  skipPolicies?: boolean;
}

/**
 * Build the SpacePolicyContext from the cached permission data + space role.
 */
async function buildSpacePolicyContext(
  userId: string,
  spaceId: string,
  action: string,
  spaceMemberRole: SpaceMembershipRole | null,
  opts?: SpacePermissionOptions
): Promise<SpacePolicyContext> {
  const data = await loadUserPermissionData(userId);
  const supabase = getSupabaseClient();
  const { data: spaceRow } = await supabase
    .from('spaces')
    .select('workspace_id')
    .eq('id', spaceId)
    .single();

  return {
    spaceId,
    workspaceId: (spaceRow?.workspace_id as string) ?? '',
    userId,
    rbacRoleIds: data.roleAssignments.map(a => a.roleId),
    spaceMembershipRole: spaceMemberRole ?? undefined,
    action,
    approvalGranted: opts?.approvalGranted,
    evidence: opts?.evidence,
  };
}

/**
 * Check if a user has a capability in the context of a specific space.
 *
 * Resolution order (most-permissive wins for grant, deny if any policy blocks):
 *   1. Space membership role → SPACE_ROLE_CAPABILITIES mapping
 *   2. Existing RBAC role_assignments (via hasCapability)
 *   3. Space-scoped policies can block (deny-overrides-grant)
 */
export async function hasCapabilityInSpace(
  userId: string,
  capabilityId: CapabilityId,
  spaceId: string,
  fallbackScope?: PermissionScope,
  opts?: SpacePermissionOptions
): Promise<boolean> {
  const spaceMemberRole = await fetchSpaceMembershipRole(userId, spaceId);

  let granted = false;
  if (spaceMemberRole && spaceRoleGrantsCapability(spaceMemberRole, capabilityId)) {
    granted = true;
  } else if (fallbackScope) {
    granted = await hasCapability(userId, capabilityId, fallbackScope);
  }

  if (!granted) return false;

  // Even if granted, a policy can block (e.g. require_approval, time_bound)
  if (opts?.skipPolicies) return true;
  const policyCtx = await buildSpacePolicyContext(userId, spaceId, capabilityId, spaceMemberRole, opts);
  const evaluation = await evaluateSpacePolicies(policyCtx);
  return !evaluation.blocked;
}

/**
 * Check a PermissionAction in a space context.
 *
 * The space membership role is checked first (fast path). If granted, space-scoped
 * policies are evaluated and can deny the action. If neither space role nor RBAC
 * grants, the action is denied with no policy evaluation needed.
 */
export async function checkPermissionInSpace(
  userId: string,
  action: PermissionAction,
  spaceId: string,
  fallbackScope?: PermissionScope,
  opts?: SpacePermissionOptions
): Promise<PermissionCheckResult> {
  const caps = getCapabilitiesForAction(action);

  const spaceMemberRole = await fetchSpaceMembershipRole(userId, spaceId);
  let baseResult: PermissionCheckResult;
  const scope: PermissionScope = fallbackScope ?? { kind: 'space', id: spaceId };

  if (spaceMemberRole) {
    const spaceGranted = caps.some((cap) => spaceRoleGrantsCapability(spaceMemberRole, cap));
    if (spaceGranted) {
      baseResult = {
        allowed: true,
        action,
        scope,
        reason: `Granted by space membership role '${spaceMemberRole}'`,
      };
    } else if (fallbackScope) {
      baseResult = await checkPermission(userId, action, fallbackScope);
    } else {
      baseResult = {
        allowed: false,
        action,
        scope,
        reason: `Space role '${spaceMemberRole}' does not grant this action`,
      };
    }
  } else if (fallbackScope) {
    baseResult = await checkPermission(userId, action, fallbackScope);
  } else {
    baseResult = {
      allowed: false,
      action,
      scope,
      reason: 'Not a space member and no RBAC role_assignment covers this action',
    };
  }

  if (!baseResult.allowed || opts?.skipPolicies) return baseResult;

  // Grant was given — let space policies have a chance to block
  const policyCtx = await buildSpacePolicyContext(userId, spaceId, action, spaceMemberRole, opts);
  const evaluation = await evaluateSpacePolicies(policyCtx);
  if (evaluation.blocked) {
    const codes = evaluation.reasons.map(r => r.code).join(', ');
    return {
      allowed: false,
      action,
      scope,
      reason: `Blocked by space policy: ${codes}`,
    };
  }

  return baseResult;
}

/**
 * Get all capabilities the user has in a space context.
 * Merges space-role capabilities with RBAC role_assignment capabilities.
 */
export async function getCapabilitiesInSpace(
  userId: string,
  spaceId: string,
  fallbackScope?: PermissionScope
): Promise<Set<CapabilityId>> {
  const result = new Set<CapabilityId>();

  const spaceMemberRole = await fetchSpaceMembershipRole(userId, spaceId);
  if (spaceMemberRole) {
    const spaceCaps = SPACE_ROLE_CAPABILITIES[spaceMemberRole];
    spaceCaps.forEach((cap) => result.add(cap));
  }

  if (fallbackScope) {
    const rbacCaps = await getResolvedCapabilitiesForUser(userId, fallbackScope);
    rbacCaps.forEach((cap) => result.add(cap));
  }

  return result;
}
